import React, { useContext, useState, useEffect } from "react";
import {
  Card,
  Typography,
  IconButton,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Box,
  Chip,
  Collapse,
} from "@mui/material";
import { MdAdd, MdDelete } from "react-icons/md";
import guestRequestApi from "../../../api/guestRequestApi";
import { ContextNotification, severitys } from "../../../App";

const DualMembers = ({ requestMembers, requestId, groupInfo, refreshGrid }) => {
  const { setOpen } = useContext(ContextNotification);

  const [otherGroupMembers, setOtherGroupMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showOtherMembers, setShowOtherMembers] = useState(false); // Toggle panel

  // Sync otherGroupMembers whenever props change
  useEffect(() => {
    const others = groupInfo.allMembers.filter(
      (m) => !requestMembers.some((r) => r.id === m.id)
    );
    setOtherGroupMembers(others);
    if (others.length === 0) setShowOtherMembers(false); // Hide panel if empty
  }, [requestMembers, groupInfo.allMembers]);

  const openConfirmDialog = (member, type) => {
    setSelectedMember(member);
    setActionType(type);
    setOpenDialog(true);
  };

  const handleConfirm = () => {
    if (!selectedMember) return;

    const apiCall =
      actionType === "removeFromRequest"
        ? guestRequestApi.AddMember(requestId, selectedMember.id)
        : guestRequestApi.DeleteMember(requestId, selectedMember.id);

    apiCall
      .then((res) => {
        if (res.data.success) {
          setOpen({
            IsEnable: true,
            Message: res.data.message,
            severity: severitys.success,
          });
          refreshGrid(true);
        } else {
          setOpen({
            IsEnable: true,
            Message: res.data.message,
            severity: severitys.error,
          });
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.errors
            ? JSON.stringify(err.response.data.errors)
            : err.response?.data
            ? JSON.stringify(err.response.data)
            : err.message
            ? err.message
            : JSON.stringify(err);
        setOpen({ IsEnable: true, Message: message, severity: severitys.error });
      });

    // Update local state for immediate UI feedback
    if (actionType === "removeFromRequest") {
      setOtherGroupMembers((prev) =>
        prev.filter((m) => m.id !== selectedMember.id)
      );
    } else {
      setOtherGroupMembers((prev) => [...prev, selectedMember]);
    }

    setOpenDialog(false);
    setSelectedMember(null);
    setActionType(null);
  };

  // Member Card Component
  const renderMemberCard = (member, isOtherGroup) => (
    <Card
      key={member.id}
      variant="outlined"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1,
        mb: 1,
        borderRadius: 2,
        backgroundColor: isOtherGroup ? "#f5f5f5" : "#e3f2fd",
        transition: "0.2s",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box>
        <Typography variant="body1" fontWeight={600}>
          {member.name} {member.family}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          شماره گذرنامه: {member.passNO}
        </Typography>
        {member.isParent && (
          <Chip
            label="سرپرست"
            size="small"
            color="info"
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
      <Tooltip title={isOtherGroup ? "افزودن به درخواست" : "حذف از درخواست"}>
        <IconButton
          size="small"
          color={isOtherGroup ? "primary" : "error"}
          onClick={() =>
            openConfirmDialog(
              member,
              isOtherGroup ? "removeFromRequest" : "addToRequest"
            )
          }
        >
          {isOtherGroup ? <MdAdd /> : <MdDelete />}
        </IconButton>
      </Tooltip>
    </Card>
  );

  return (
    <>
      <Grid container spacing={2}>
        {/* Members in Request */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: "#e3f2fd",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#1976d2" }}
              >
                اعضای درخواست
              </Typography>

              {/* Show button only if there are other members */}
              {otherGroupMembers.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowOtherMembers((prev) => !prev)}
                  endIcon={
                    <MdAdd
                      style={{
                        transform: showOtherMembers
                          ? "rotate(45deg)" // Rotates arrow when panel is open
                          : "rotate(0deg)",
                        transition: "0.3s",
                      }}
                    />
                  }
                >
                  افزودن عضو
                </Button>
              )}
            </Box>

            {requestMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                هیچ عضوی در درخواست نیست.
              </Typography>
            ) : (
              requestMembers.map((member) => renderMemberCard(member, false))
            )}
          </Paper>
        </Grid>

        {/* Other Group Members with smooth collapse */}
        <Grid item xs={12} md={6}>
          <Collapse in={showOtherMembers}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                maxHeight: 300,
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1, color: "#616161" }}
              >
                سایر اعضای گروه
              </Typography>
              {otherGroupMembers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  هیچ عضو دیگری وجود ندارد.
                </Typography>
              ) : (
                otherGroupMembers.map((member) =>
                  renderMemberCard(member, true)
                )
              )}
            </Paper>
          </Collapse>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>تأیید عملیات</DialogTitle>
        <DialogContent>
          <Typography>
            {actionType === "addToRequest"
              ? `آیا ${selectedMember?.name} ${selectedMember?.family} از درخواست حذف شود؟`
              : `آیا ${selectedMember?.name} ${selectedMember?.family} به لیست درخواست افزوده شود؟`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>انصراف</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            تأیید
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DualMembers;
